import { useEffect } from 'react';
import { AppModal, AppButton, AppForm } from '@/components/atoms';
import { RA2NDatePicker } from '@/components/organisms/RA2NDatePicker';
import { Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import useAppModal from '@/hooks/useAppModal';
import {
  resubmissionMonthSchema,
  type ResubmissionMonthFormValues,
} from '@/schemas/returnRezeptSchema';
import { convertSeirekiWrap, convertWarekiMonth } from '@/utils/wareki';

// HIT-3536: default 再提出年月 to next month. RA2NDatePicker (picker="month")
// expects the wareki "{era}YY年MM月" display string (e.g. 令和08年06月).
const nextMonthWareki = (): string => {
  const now = new Date();
  const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const eraYear = convertWarekiMonth(next.getFullYear(), next.getMonth() + 1);
  if (!eraYear) return '';
  return `${eraYear}年${String(next.getMonth() + 1).padStart(2, '0')}月`;
};

interface ViolatingPatient {
  serial_number: string | number;
  patient_name: string;
}

interface ResubmissionMonthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (yearmonthStr: string) => void;
  isSubmitting: boolean;
  violatingPatients?: ViolatingPatient[];
  serverError?: string;
}

export default function ResubmissionMonthModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  violatingPatients,
  serverError,
}: ResubmissionMonthModalProps) {
  const appModal = useAppModal();
  const { control, handleSubmit, getValues, reset } = useTrimmedForm<ResubmissionMonthFormValues>({
    resolver: zodResolver(resubmissionMonthSchema),
    defaultValues: { rezept_yearmonth_str: nextMonthWareki() },
  });

  useEffect(() => {
    if (isOpen) {
      // HIT-3536: default to next month each open (instead of empty placeholder).
      reset({ rezept_yearmonth_str: nextMonthWareki() });
    }
  }, [isOpen, reset]);

  const handleFormSubmit = (values: ResubmissionMonthFormValues) => {
    const seireki = convertSeirekiWrap(values.rezept_yearmonth_str);
    const ym = seireki.replace(/\D/g, '');
    onSubmit(ym);
  };

  const handleApply = () => {
    if (!getValues('rezept_yearmonth_str')?.trim()) {
      appModal.warning({ title: '日付を入力してください。', okText: 'OK' });
      return;
    }

    void handleSubmit(handleFormSubmit)();
  };

  return (
    <AppModal
      isOpen={isOpen}
      onClose={onClose}
      title="再提出年月登録"
      maxWidth={500}
      footer={
        <div className="flex justify-end gap-2">
          <AppButton onClick={onClose}>閉じる</AppButton>
          <AppButton type="primary" onClick={handleApply} loading={isSubmitting}>
            適用
          </AppButton>
        </div>
      }
    >
      <form
        onSubmit={e => {
          e.preventDefault();
          handleApply();
        }}
      >
        <div className="mb-4">
          <div className="flex items-center gap-3">
            <label className="whitespace-nowrap font-medium">再提出年月</label>
            <Controller
              name="rezept_yearmonth_str"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <AppForm.Item
                  validateStatus={error ? 'error' : undefined}
                  help={error?.message}
                  className="mb-0 flex-1"
                >
                  <RA2NDatePicker
                    picker="month"
                    value={field.value || undefined}
                    onChange={(val: string | null) => field.onChange(val ?? '')}
                    placeholder="年月指定"
                    className="w-full"
                    error={!!error}
                  />
                </AppForm.Item>
              )}
            />
          </div>
          {(serverError || (violatingPatients && violatingPatients.length > 0)) && (
            <div className="text-red-500 mt-1 text-sm">
              {serverError && <div>{serverError}</div>}
              {violatingPatients &&
                violatingPatients.length > 0 &&
                violatingPatients.map((p, i) => (
                  <div key={i}>
                    受診者 {p.serial_number} {p.patient_name}
                  </div>
                ))}
            </div>
          )}
        </div>
      </form>
    </AppModal>
  );
}
